import { COMPETITION_ROLE_VALUES } from '@napaicp/shared'
import { z } from 'zod'
import { displayNameSchema, emailSchema, publicIdSchema, utcTimestampSchema } from './primitives'

export const competitionRoleNameSchema = z.enum(COMPETITION_ROLE_VALUES)

export const competitionRoleSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  userId: publicIdSchema,
  role: competitionRoleNameSchema,
  grantedByUserId: publicIdSchema.nullable(),
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
})

export type CompetitionRoleRecord = z.infer<typeof competitionRoleSchema>

/**
 * A role grant as shown on the competition's people screen. The email is
 * included because an administrator managing access has to be able to tell two
 * people with the same display name apart — this DTO is only ever returned to
 * callers holding `role.read`.
 */
export const competitionRoleDtoSchema = z.object({
  id: publicIdSchema,
  role: competitionRoleNameSchema,
  user: z.object({
    id: publicIdSchema,
    email: emailSchema,
    displayName: displayNameSchema,
  }),
  grantedByUserId: publicIdSchema.nullable(),
  createdAt: utcTimestampSchema,
})

export type CompetitionRoleDto = z.infer<typeof competitionRoleDtoSchema>

/**
 * Grants are made by email rather than by user id: an administrator inviting a
 * judge knows their address, not their internal identifier, and exposing a user
 * lookup endpoint would let any competition admin enumerate the platform's
 * accounts.
 */
export const grantCompetitionRoleRequestSchema = z.strictObject({
  email: emailSchema,
  role: competitionRoleNameSchema,
})

export type GrantCompetitionRoleRequest = z.infer<typeof grantCompetitionRoleRequestSchema>
